import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Bell, 
  Mail, 
  MessageSquare,
  Settings,
  Check,
  X,
  Clock,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const NotificacoesPDI = ({ colaboradorId, pdiId }) => {
  const [preferencias, setPreferencias] = useState({
    email_ativado: true,
    notificar_prazo_proximo: true,
    notificar_acao_atrasada: true,
    notificar_nova_avaliacao: true,
    notificar_mentoria_agendada: true,
    notificar_feedback_recebido: true,
    frequencia: 'imediato' // imediato, diario, semanal
  });
  const [mostrarModal, setMostrarModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notificacoesRecentes, setNotificacoesRecentes] = useState([]);

  useEffect(() => {
    loadPreferencias();
    loadNotificacoesRecentes();
  }, [colaboradorId]);

  const loadPreferencias = async () => {
    try {
      // Carregar preferências salvas (mock por enquanto)
      const saved = localStorage.getItem(`notif_prefs_${colaboradorId}`);
      if (saved) {
        setPreferencias(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Erro ao carregar preferências:', error);
    }
  };

  const loadNotificacoesRecentes = async () => {
    try {
      // Buscar notificações recentes do backend
      const response = await axios.get(`${API}/api/pdi-completo/notificacoes/${colaboradorId}`);
      const data = response.data;
      // Garantir que sempre seja um array
      setNotificacoesRecentes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
      // Mock de notificações para demonstração
      setNotificacoesRecentes([
        {
          id: '1',
          tipo: 'prazo_proximo',
          mensagem: 'A ação "Curso de Liderança" vence em 3 dias',
          canal: 'email',
          status: 'enviado',
          enviado_em: new Date().toISOString()
        },
        {
          id: '2',
          tipo: 'mentoria_agendada',
          mensagem: 'Mentoria agendada para amanhã às 14h',
          canal: 'email',
          status: 'enviado',
          enviado_em: new Date(Date.now() - 86400000).toISOString()
        }
      ]);
    }
  };

  const salvarPreferencias = async () => {
    try {
      setLoading(true);
      
      // Salvar localmente
      localStorage.setItem(`notif_prefs_${colaboradorId}`, JSON.stringify(preferencias));
      
      // Enviar para backend (implementar endpoint se necessário)
      // await axios.post(`${API}/api/pdi-completo/notificacoes/preferencias`, preferencias);
      
      toast.success('Preferências salvas com sucesso!');
      setMostrarModal(false);
    } catch (error) {
      console.error('Erro ao salvar preferências:', error);
      toast.error('Erro ao salvar preferências');
    } finally {
      setLoading(false);
    }
  };

  const enviarNotificacaoTeste = async () => {
    try {
      setLoading(true);
      
      await axios.post(`${API}/api/pdi-completo/notificacoes/enviar`, null, {
        params: {
          colaborador_id: colaboradorId,
          tipo: 'teste',
          mensagem: 'Esta é uma notificação de teste do sistema PDI!',
          canal: 'email'
        }
      });
      
      toast.success('Notificação de teste enviada! Verifique seu email.', {
        icon: <Mail className="h-4 w-4" />
      });
      
      await loadNotificacoesRecentes();
    } catch (error) {
      console.error('Erro ao enviar notificação:', error);
      toast.error('Erro ao enviar notificação');
    } finally {
      setLoading(false);
    }
  };

  const getIconeTipo = (tipo) => {
    const icons = {
      prazo_proximo: <Clock className="h-4 w-4 text-orange-600" />,
      acao_atrasada: <AlertCircle className="h-4 w-4 text-red-600" />,
      mentoria_agendada: <CheckCircle className="h-4 w-4 text-green-600" />,
      feedback_recebido: <MessageSquare className="h-4 w-4 text-blue-600" />,
      teste: <Bell className="h-4 w-4 text-purple-600" />
    };
    return icons[tipo] || <Bell className="h-4 w-4" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 60) return `${diffMins} minutos atrás`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} horas atrás`;
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-600 rounded-full">
                <Bell className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-1">Notificações do PDI</h2>
                <p className="text-gray-600">
                  Fique por dentro de prazos, mentorias e feedbacks
                </p>
              </div>
            </div>
            <Button 
              onClick={() => setMostrarModal(true)}
              variant="outline"
            >
              <Settings className="h-4 w-4 mr-2" />
              Preferências
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Canais Ativos */}
      <Card>
        <CardHeader>
          <CardTitle>Canal de Notificação Ativo</CardTitle>
        </CardHeader>
        <CardContent>
          <Card className={`${preferencias.email_ativado ? 'bg-green-50 border-green-300' : 'bg-gray-50'} border-2`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Mail className={`h-6 w-6 ${preferencias.email_ativado ? 'text-green-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-semibold">Email</p>
                    <p className="text-xs text-gray-600">Notificações por email corporativo</p>
                  </div>
                </div>
                {preferencias.email_ativado ? (
                  <Check className="h-5 w-5 text-green-600" />
                ) : (
                  <X className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      {/* Notificações Recentes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Notificações Recentes</span>
            <Button 
              size="sm" 
              onClick={enviarNotificacaoTeste}
              disabled={loading}
              variant="outline"
            >
              <Bell className="h-4 w-4 mr-2" />
              Enviar Teste
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {notificacoesRecentes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Nenhuma notificação ainda</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notificacoesRecentes.map((notif) => (
                <Card key={notif.id} className="hover:bg-gray-50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start space-x-3">
                      <div className="mt-1">
                        {getIconeTipo(notif.tipo)}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{notif.mensagem}</p>
                        <div className="flex items-center space-x-3 mt-1">
                          <span className="text-xs text-gray-600">{formatDate(notif.enviado_em)}</span>
                          <Badge variant="outline" className="text-xs">
                            {notif.canal}
                          </Badge>
                          <Badge className={`text-xs ${
                            notif.status === 'enviado' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {notif.status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Preferências */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="bg-blue-50 border-b">
              <CardTitle className="flex items-center space-x-2">
                <Settings className="h-5 w-5 text-blue-600" />
                <span>Preferências de Notificações</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Canal Email */}
              <div>
                <h4 className="font-semibold mb-3">Canal de Notificação</h4>
                <label className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferencias.email_ativado}
                    onChange={(e) => setPreferencias({...preferencias, email_ativado: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <Mail className="h-5 w-5 text-blue-600" />
                  <div className="flex-1">
                    <p className="font-medium">Email Corporativo</p>
                    <p className="text-xs text-gray-600">Receber notificações por email</p>
                  </div>
                </label>
              </div>

              {/* Tipos de Notificação */}
              <div>
                <h4 className="font-semibold mb-3">Tipos de Notificação</h4>
                <div className="space-y-2">
                  {[
                    { key: 'notificar_prazo_proximo', label: 'Prazos próximos', desc: 'Avisar quando ações estiverem próximas do prazo' },
                    { key: 'notificar_acao_atrasada', label: 'Ações atrasadas', desc: 'Alertar sobre ações com prazo vencido' },
                    { key: 'notificar_nova_avaliacao', label: 'Novas avaliações', desc: 'Quando houver nova avaliação de resultados' },
                    { key: 'notificar_mentoria_agendada', label: 'Mentorias agendadas', desc: 'Lembrete de sessões de mentoria' },
                    { key: 'notificar_feedback_recebido', label: 'Feedbacks recebidos', desc: 'Quando receber feedback do gestor' }
                  ].map((item) => (
                    <label key={item.key} className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferencias[item.key]}
                        onChange={(e) => setPreferencias({...preferencias, [item.key]: e.target.checked})}
                        className="w-4 h-4 mt-1"
                      />
                      <div>
                        <p className="font-medium text-sm">{item.label}</p>
                        <p className="text-xs text-gray-600">{item.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Frequência */}
              <div>
                <h4 className="font-semibold mb-3">Frequência de Notificações</h4>
                <select
                  value={preferencias.frequencia}
                  onChange={(e) => setPreferencias({...preferencias, frequencia: e.target.value})}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="imediato">Imediato (receber assim que ocorrer)</option>
                  <option value="diario">Resumo Diário (1x por dia)</option>
                  <option value="semanal">Resumo Semanal (1x por semana)</option>
                </select>
              </div>

              {/* Botões */}
              <div className="flex space-x-3 pt-4 border-t">
                <Button 
                  onClick={salvarPreferencias}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? 'Salvando...' : 'Salvar Preferências'}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setMostrarModal(false)}
                >
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default NotificacoesPDI;
